import * as THREE from 'three'

import {
  EMISSIVE_COLOR_MATERIALS,
  ObjectRuntimeState,
  RobloxMaterial,
  SceneObjectDescriptor,
} from '../types'

/**
 * 巨大シーン（RJS08 = 10,490 box 等）の描画コストを下げるための、静的 box 専用の
 * インスタンス化バッチャ。
 *
 * 個別メッシュ（1 オブジェクト = 1 draw call）だと数千〜万のドローコールで fps が
 * 大きく落ちる。ここでは「空間チャンク × Roblox マテリアル」ごとに 1 つの
 * `InstancedMesh` へ集約し、ドローコールをグループ数まで圧縮する。各インスタンスは
 * 行列（位置/姿勢/サイズ）と色（instanceColor）を個別に持つ。チャンク単位で
 * カメラからの距離カリングを行い、遠景チャンクは描画しない。
 *
 * バッチ対象外（アバター・GLB/mesh・decal 付き・半透明・Neon・非静的）は従来どおり
 * 個別メッシュで描画する（呼び出し側で振り分ける）。
 */

/** 単位 box（全インスタンス共有）。インスタンス行列の scale で実寸へ広げる。 */
const UNIT_BOX_GEOMETRY = new THREE.BoxGeometry(1, 1, 1)

/** 非表示インスタンス用の退避行列（scale 0 でラスタライズ対象から外す）。 */
const HIDDEN_MATRIX = new THREE.Matrix4().makeScale(0, 0, 0)

const IDENTITY_ORIENTATION = { x: 0, y: 0, z: 0, w: 1 }

export interface BatchableObject {
  readonly id: string
  readonly descriptor: SceneObjectDescriptor
}

export interface StaticBoxBatcherOptions {
  readonly scene: THREE.Scene
  /** Roblox マテリアル別の共有マテリアル（スタッドシェーダ込み）を生成する。 */
  readonly createBucketMaterial: (material: RobloxMaterial) => THREE.Material
  /** チャンク 1 辺の studs。距離カリングの粒度とグループ（ドローコール）数を決める。 */
  readonly chunkSize: number
  /** カメラ（XZ 平面）からこの距離（studs）を超えたチャンクは描画しない。 */
  readonly cullDistance: number
}

export interface StaticBoxBatcher {
  /** この objectId がバッチ管理下か。 */
  has(id: string): boolean
  /** 管理下インスタンス数（destroy 検出のための照合に使う）。 */
  readonly size: number
  /** World 状態をインスタンス（行列・色・可視）へ反映する。 */
  setState(id: string, state: ObjectRuntimeState): void
  /** states に存在しない管理下インスタンスを解放する（destroy の照合）。 */
  reconcile(states: ReadonlyMap<string, ObjectRuntimeState>): void
  /** インスタンスを非表示にして解放する（destroy 用）。 */
  remove(id: string): void
  /** raycast ヒット（InstancedMesh + instanceId）から objectId を逆引きする。 */
  resolveObjectId(mesh: THREE.Object3D, instanceId: number): string | null
  /** raycast 対象に渡す InstancedMesh 群。 */
  readonly meshes: readonly THREE.InstancedMesh[]
  /**
   * チャンク単位の距離カリングを反映する。focalDistance はカメラと注視点の距離で、
   * ズームアウト時に閾値を広げて可視チャンクの誤カリングを防ぐために使う。
   */
  updateCulling(cameraPosition: THREE.Vector3, focalDistance: number): void
  /** 全 InstancedMesh / マテリアルを解放する。 */
  dispose(): void
}

/**
 * バッチ可能な descriptor か判定する。
 * 静的・box・外部モデルなし・decal/看板テキストなし・不透明・非 Neon のみ集約する
 * （半透明は per-instance 不透明度が、Neon は per-instance 発光が instancing で
 * 表現しづらく、decal/surfaceText は面オーバーレイメッシュが必要なため個別メッシュへ回す）。
 */
export const isBatchableDescriptor = (d: SceneObjectDescriptor): boolean =>
  d.isStatic === true &&
  (d.shape ?? 'box') === 'box' &&
  d.modelUrl === undefined &&
  d.meshUrl === undefined &&
  (d.decals === undefined || d.decals.length === 0) &&
  (d.surfaceText === undefined || d.surfaceText.length === 0) &&
  !d.transparency &&
  !EMISSIVE_COLOR_MATERIALS.has(d.material ?? 'Plastic') &&
  // キラキラ（Sparkles）・炎（Fire）はエフェクト用 MeshEntry が必要なため
  // 個別メッシュへ回す。
  d.sparkle !== true &&
  d.fire !== true

interface BatchGroup {
  readonly mesh: THREE.InstancedMesh
  /** チャンク中心の XZ（距離カリング判定用）。 */
  readonly centerX: number
  readonly centerZ: number
  /** 割当済みインスタンス数（容量確保のカウンタ）。 */
  assigned: number
  /**
   * チャンク中心から、所属インスタンスの最遠コーナーまでの距離（studs）。
   * チャンクより大きい box が外へ張り出していても、距離カリングで誤って消さない
   * ようにこの半径ぶん判定を緩める。
   */
  cullRadius: number
}

interface InstanceSlot {
  readonly group: BatchGroup
  readonly index: number
  /**
   * 直近に反映した state の参照。World は不変更新（変化時のみ新オブジェクト）のため、
   * 参照一致なら未変更とみなし、毎フレームの再アップロードを避ける。
   */
  lastState: ObjectRuntimeState | null
}

export const createStaticBoxBatcher = (
  objects: readonly BatchableObject[],
  options: StaticBoxBatcherOptions,
): StaticBoxBatcher => {
  const { scene, createBucketMaterial, chunkSize, cullDistance } = options

  const groupKeyOf = (d: SceneObjectDescriptor): string => {
    const cx = Math.floor(d.position.x / chunkSize)
    const cz = Math.floor(d.position.z / chunkSize)
    const material = d.material ?? 'Plastic'
    return `${cx}|${cz}|${material}`
  }

  // (1) グループごとのインスタンス数を数える（InstancedMesh は固定容量のため）。
  const counts = new Map<string, number>()
  for (const { descriptor } of objects) {
    const key = groupKeyOf(descriptor)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  // (2) Roblox マテリアル別の共有マテリアルを生成（バケット間で再利用）。
  const materialByKey = new Map<RobloxMaterial, THREE.Material>()
  const getBucketMaterial = (material: RobloxMaterial): THREE.Material => {
    let mat = materialByKey.get(material)
    if (!mat) {
      mat = createBucketMaterial(material)
      materialByKey.set(material, mat)
    }
    return mat
  }

  // (3) グループ（InstancedMesh）を生成して scene へ追加する。
  const groups = new Map<string, BatchGroup>()
  for (const [key, count] of counts) {
    const [cxRaw, czRaw, materialRaw] = key.split('|')
    const cx = Number(cxRaw)
    const cz = Number(czRaw)
    const material = materialRaw as RobloxMaterial
    const mesh = new THREE.InstancedMesh(
      UNIT_BOX_GEOMETRY,
      getBucketMaterial(material),
      count,
    )
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage)
    // instanceColor を確保（既定は白＝マテリアル色そのまま）。
    const colors = new Float32Array(count * 3).fill(1)
    mesh.instanceColor = new THREE.InstancedBufferAttribute(colors, 3)
    mesh.instanceColor.setUsage(THREE.DynamicDrawUsage)
    // 距離カリングを自前で行うため three.js の per-mesh フラスタムカリングは無効化
    // （InstancedMesh の境界球は単位 box のままで正しくカリングできないため）。
    mesh.frustumCulled = false
    const group: BatchGroup = {
      mesh,
      centerX: (cx + 0.5) * chunkSize,
      centerZ: (cz + 0.5) * chunkSize,
      assigned: 0,
      cullRadius: 0,
    }
    groups.set(key, group)
    scene.add(mesh)
  }

  // (4) 各オブジェクトへインスタンススロットを割り当て、グループの cullRadius を更新する。
  const slotById = new Map<string, InstanceSlot>()
  const idsByMesh = new Map<THREE.InstancedMesh, (string | undefined)[]>()
  // 現在バッチで描画中（active）の id。destroy / demote で外し、再出現で戻す。
  const activeIds = new Set<string>()
  for (const { id, descriptor } of objects) {
    const group = groups.get(groupKeyOf(descriptor))
    if (!group) {
      continue
    }
    const index = group.assigned
    group.assigned += 1
    slotById.set(id, { group, index, lastState: null })
    activeIds.add(id)
    let ids = idsByMesh.get(group.mesh)
    if (!ids) {
      ids = []
      idsByMesh.set(group.mesh, ids)
    }
    ids[index] = id
    // チャンク中心からこの box の最遠コーナーまでの距離でカリング半径を広げる。
    const dx = descriptor.position.x - group.centerX
    const dz = descriptor.position.z - group.centerZ
    const reach =
      Math.hypot(dx, dz) +
      0.5 * Math.hypot(descriptor.size.x, descriptor.size.z)
    if (reach > group.cullRadius) {
      group.cullRadius = reach
    }
  }

  const meshes: readonly THREE.InstancedMesh[] = [...groups.values()].map(
    (g) => g.mesh,
  )

  // 毎フレーム/更新で使い回す一時オブジェクト（allocation 回避）。
  const tmpPosition = new THREE.Vector3()
  const tmpQuaternion = new THREE.Quaternion()
  const tmpYaw = new THREE.Quaternion()
  const tmpBase = new THREE.Quaternion()
  const tmpScale = new THREE.Vector3()
  const tmpMatrix = new THREE.Matrix4()
  const tmpColor = new THREE.Color()
  const Y_AXIS = new THREE.Vector3(0, 1, 0)

  const has = (id: string): boolean => slotById.has(id)

  const setState = (id: string, state: ObjectRuntimeState): void => {
    const slot = slotById.get(id)
    if (!slot) {
      return
    }
    // demote / destroy で外れていたインスタンスを再びバッチ描画へ戻す
    // （reset で同じ id が復活したケース等）。次の比較を確実に走らせる。
    if (!activeIds.has(id)) {
      activeIds.add(id)
      const ids = idsByMesh.get(slot.group.mesh)
      if (ids) {
        ids[slot.index] = id
      }
      slot.lastState = null
    }
    // 未変更（同一 state 参照）なら再アップロードしない。静的 box は初回のみ反映され、
    // 以降のフレームは早期 return するため毎フレームのバッファ転送が起きない。
    if (slot.lastState === state) {
      return
    }
    slot.lastState = state
    const { mesh } = slot.group
    if (!state.visible) {
      mesh.setMatrixAt(slot.index, HIDDEN_MATRIX)
      mesh.instanceMatrix.needsUpdate = true
      return
    }
    tmpPosition.set(state.position.x, state.position.y, state.position.z)
    // 最終姿勢 = Y ヨー(rotationY) ∘ 基準向き(orientation)。applyState と同一規則。
    tmpYaw.setFromAxisAngle(Y_AXIS, state.rotationY)
    const o = state.orientation ?? IDENTITY_ORIENTATION
    tmpBase.set(o.x, o.y, o.z, o.w)
    tmpQuaternion.copy(tmpYaw).multiply(tmpBase)
    tmpScale.set(state.size.x, state.size.y, state.size.z)
    tmpMatrix.compose(tmpPosition, tmpQuaternion, tmpScale)
    mesh.setMatrixAt(slot.index, tmpMatrix)
    mesh.instanceMatrix.needsUpdate = true
    tmpColor.set(state.color)
    mesh.setColorAt(slot.index, tmpColor)
    if (mesh.instanceColor) {
      mesh.instanceColor.needsUpdate = true
    }
    // 実行時に移動/リサイズされても距離カリングで誤って消さないよう、現在状態の
    // 到達半径でグループの cullRadius を単調増加させる（縮めるとちらつくため）。
    const dx = state.position.x - slot.group.centerX
    const dz = state.position.z - slot.group.centerZ
    const reach =
      Math.hypot(dx, dz) +
      0.5 * Math.hypot(state.size.x, state.size.y, state.size.z)
    if (reach > slot.group.cullRadius) {
      slot.group.cullRadius = reach
    }
  }

  const remove = (id: string): void => {
    const slot = slotById.get(id)
    if (!slot || !activeIds.delete(id)) {
      return
    }
    // スロット（容量）は保持したままインスタンスを非表示化し、idsByMesh からも外す。
    // reset 等で同じ id が復活したら setState が再活性化する。
    slot.lastState = null
    slot.group.mesh.setMatrixAt(slot.index, HIDDEN_MATRIX)
    slot.group.mesh.instanceMatrix.needsUpdate = true
    const ids = idsByMesh.get(slot.group.mesh)
    if (ids) {
      ids[slot.index] = undefined
    }
  }

  const reconcile = (states: ReadonlyMap<string, ObjectRuntimeState>): void => {
    // states から消えた（destroy された）アクティブインスタンスを解放する。
    // 呼び出し側は「states 内のアクティブ数 < size」のとき（=destroy 発生時）だけ
    // 呼ぶため、通常フレームでは走らない。
    const stale: string[] = []
    for (const id of activeIds) {
      if (!states.has(id)) {
        stale.push(id)
      }
    }
    for (const id of stale) {
      remove(id)
    }
  }

  const resolveObjectId = (
    mesh: THREE.Object3D,
    instanceId: number,
  ): string | null => {
    if (!(mesh instanceof THREE.InstancedMesh)) {
      return null
    }
    return idsByMesh.get(mesh)?.[instanceId] ?? null
  }

  const updateCulling = (
    cameraPosition: THREE.Vector3,
    focalDistance: number,
  ): void => {
    // free/俯瞰視点ではカメラが注視点から大きく離れる（ズームアウト）。その分だけ
    // 閾値を広げ、見えているチャンクを誤って消さない（follow/接近時は focalDistance が
    // 小さく、従来どおり遠景チャンクを間引く）。
    const reach = cullDistance + Math.max(0, focalDistance)
    for (const group of groups.values()) {
      const dx = group.centerX - cameraPosition.x
      const dz = group.centerZ - cameraPosition.z
      // チャンクの張り出し（cullRadius）ぶんも閾値を緩め、大きい box を誤って消さない。
      group.mesh.visible = Math.hypot(dx, dz) <= reach + group.cullRadius
    }
  }

  const dispose = (): void => {
    for (const group of groups.values()) {
      scene.remove(group.mesh)
      group.mesh.dispose()
    }
    for (const material of materialByKey.values()) {
      material.dispose()
    }
    // UNIT_BOX_GEOMETRY はモジュール共有のため dispose しない。
    groups.clear()
    slotById.clear()
    idsByMesh.clear()
    materialByKey.clear()
  }

  return {
    has,
    get size() {
      return activeIds.size
    },
    setState,
    reconcile,
    remove,
    resolveObjectId,
    meshes,
    updateCulling,
    dispose,
  }
}
